import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * The project this viewer last worked in, so an in-app card can open there.
 *
 * Keyed to the viewer: the entry carries the `user_id` and `tenant_id` of the
 * session that wrote it, and {@link rememberedSlugFor} ignores it for any
 * other session, so a shared browser never offers one person's project to the
 * next. Only an explicit sign-out clears it: `useLogout` clears it in
 * `onSettled`, so a failed sign-out request clears it too. An expired session
 * leaves it in place, where the identity check above still keeps it from
 * anyone else. It is a preference, never an
 * authority: a caller intersects the slug with `GET /api/projects` before
 * using it, and an entry that fails that is simply not used.
 *
 * Written by `useRememberProject` (`api/projects.ts`) from every page under
 * `/projects/:slug`.
 */
export interface LastProject {
  user_id: string;
  tenant_id: string;
  slug: string;
}

interface LastProjectState {
  last: LastProject | null;
  remember: (entry: LastProject) => void;
  clear: () => void;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value !== "";
}

/**
 * `value` as a {@link LastProject}, or null when it is not one. localStorage
 * is user-editable, so a rehydrated entry is checked before it is compared.
 */
export function asLastProject(value: unknown): LastProject | null {
  if (typeof value !== "object" || value === null) return null;
  const { user_id, tenant_id, slug } = value as Record<string, unknown>;
  if (!nonEmptyString(user_id) || !nonEmptyString(tenant_id) || !nonEmptyString(slug)) return null;
  return { user_id, tenant_id, slug };
}

export const useLastProjectStore = create<LastProjectState>()(
  persist(
    (set) => ({
      last: null,
      remember: (entry) => set({ last: entry }),
      clear: () => set({ last: null }),
    }),
    {
      name: "flowstack-last-project",
      version: 1,
      // An unversioned (pre-1) entry goes through `merge` like any other.
      migrate: (persisted) => persisted as LastProjectState,
      // Every rehydration, at any version, keeps only an entry that parses.
      merge: (persisted, current) => ({
        ...current,
        last: asLastProject((persisted as { last?: unknown } | null)?.last),
      }),
    }
  )
);

/** The remembered slug, only when it was written by this same viewer. */
export function rememberedSlugFor(
  last: LastProject | null,
  session: { user_id: string; tenant_id: string } | null | undefined
): string | null {
  const entry = asLastProject(last);
  if (!entry || !session) return null;
  if (entry.user_id !== session.user_id || entry.tenant_id !== session.tenant_id) return null;
  return entry.slug;
}
